// Board table columns + the per-row action confirmations. Split out of board.tsx; `buildColumns`
// is a factory the board calls with the handlers it owns (busy state, save/reload, drawer openers).
import { EnvironmentOutlined, ExclamationCircleOutlined, ExportOutlined } from "@ant-design/icons";
import { Button, Dropdown, Input, message, Modal, Select, Space, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useState } from "react";
import CopyText from "../components/copy_text";
import ExpressTag from "../components/express_tag";
import UnloadingTag from "../components/unloading_tag";
import { fmtIST, googleMapsUrl, omsOrderPath } from "../format";
import { AvailableAction, BookingEscalation, LogisticsOrderRow } from "../types";
import {
  markDeliveryAttempted,
  requeueBooking,
  runLogisticsAction,
  TeamMember,
  updateLogisticsOrder,
} from "./logistics.api";
import { AttentionTag, EscalationTag, SlaTag } from "./components/board_meta";
import { DeliveryStatusTag } from "./components/logistics_status_tag";

export const dash = (v?: string | null) => v || <span style={{ color: "#bbb" }}>—</span>;

// Last-mile (customer-bound) bookings only — shipments, dropships, returns. "Mark delivery attempted"
// is offered across the whole last-mile lane; first-mile / middle legs (receive/transfer/pick/pack)
// have no delivery to attempt, and the backend 400s anything it can't take, so the menu stays clean.
const isDeliveryLeg = (r: LogisticsOrderRow) => r.board_lane === "last_mile";

// The mandatory reason box inside the "Mark delivery attempted" confirm. Owns its own state and reports
// each keystroke up to the captured variable the modal's onOk reads (and validates).
const AttemptReason: React.FC<{ onChange: (v: string) => void }> = ({ onChange }) => {
  const [value, setValue] = useState("");
  return (
    <Input.TextArea
      rows={3}
      autoFocus
      style={{ marginTop: 12 }}
      placeholder="Reason (required) — e.g. customer unavailable, address not found"
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
        onChange(e.target.value);
      }}
    />
  );
};

// A From / To cell: the endpoint label, truncated to the column width (full text on hover), linking
// to Google Maps at the PRECISE lat/long we store (falling back to an address-text search). Stops
// propagation so opening the map doesn't also trigger the row's contents modal.
export const locationCell = (label: string | null, lat: number | null, long: number | null) => {
  const url = googleMapsUrl({ lat, long }, [label]);
  const text = label || "—";
  // A single truncating line (icon inline so it's part of the ellipsis flow, not a flex sibling that
  // steals the text's bounded width).
  const line = (
    <span
      style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
    >
      <EnvironmentOutlined style={{ color: "#1677ff", marginRight: 4 }} />
      {text}
    </span>
  );
  const el = url ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ color: "inherit", display: "block" }}
    >
      {line}
    </a>
  ) : (
    line
  );
  return <Tooltip title={text}>{el}</Tooltip>;
};

// The Owner dropdown — sets (or clears) `assigned_to` on one booking. Its own tiny component so it
// can carry a per-row saving state; stops propagation so using it doesn't open the row modal.
const OwnerCell: React.FC<{
  row: LogisticsOrderRow;
  teamMembers: TeamMember[];
  onSaved: (row: LogisticsOrderRow) => void;
}> = ({ row, teamMembers, onSaved }) => {
  const [saving, setSaving] = useState(false);
  // `val` is a team member's user id (or undefined on clear → null unassigns on the server).
  const change = async (val?: string) => {
    setSaving(true);
    const updated = await updateLogisticsOrder(row.id, { assigned_to: val ?? null });
    setSaving(false);
    if (updated) onSaved(updated);
  };
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Select
        size="small"
        style={{ width: 130 }}
        placeholder="Unassigned"
        value={row.assigned_to ?? undefined}
        loading={saving}
        allowClear
        showSearch
        options={teamMembers.map((m) => ({ value: m.value, label: m.label }))}
        onChange={(v) => change(v)}
        onClear={() => change(undefined)}
        filterOption={(input, opt) =>
          String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
        }
      />
    </span>
  );
};

// Short carrier label + tag colour per provider (worded, not colour-only).
export const CARRIER_META: Record<string, { label: string; color?: string }> = {
  own_tms: { label: "Own fleet", color: "blue" },
  porter: { label: "Porter", color: "purple" },
  intercity: { label: "Intercity", color: "geekblue" },
  supplier_managed: { label: "Supplier", color: "default" },
};

export const dispatchLabel = (row: LogisticsOrderRow) => {
  const s = row.target_dispatch_start;
  if (!s) return <span style={{ color: "#bbb" }}>—</span>;
  const end = row.target_dispatch_end;
  return (
    <div style={{ fontSize: 12, whiteSpace: "nowrap" }}>
      <div>{fmtIST(s, "DD MMM")}</div>
      <div style={{ color: "#888" }}>
        {fmtIST(s, "HH:mm")}
        {end ? `–${fmtIST(end, "HH:mm")}` : ""}
      </div>
    </div>
  );
};

// The escalation column's cell: WHAT the truck was flagged for, so a quarantined row always carries
// its reason instead of just being missing from to-assign. On THIS board it is mounted only for the
// escalation section (see `buildColumns`'s `showEscalation`) — a permanent column would spend 260px
// on a field that is null for all but a handful of rows. The Vendor Dashboard has no sections, so it
// mounts this for every row and imports the cell from here rather than growing a second copy.
// Closing one out is delivery + procurement work (the `oms.update` permission — see
// `canResolveEscalation`), so everyone else reads the escalation without the button: the row still
// says what it was flagged for, it just isn't theirs to close.
// Generic over the row: it reads nothing but `escalation`, so the Missing Orders section (whose rows
// are LEGS, not bookings) renders the identical cell rather than growing a second copy of it.
export const escalationCell = <T extends { escalation: BookingEscalation | null }>(
  r: T,
  onResolve: (r: T) => void,
  canResolve: boolean
) => {
  const esc = r.escalation;
  if (!esc) return <span style={{ color: "#bbb" }}>—</span>;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontWeight: 600, color: "#cf1322" }}>
        {esc.issue_type || "Escalation"}
        {esc.count > 1 && (
          <span style={{ color: "#8c8c8c", fontWeight: 400 }}> · {esc.count}× raised</span>
        )}
      </div>
      {esc.remarks && (
        <Tooltip title={esc.remarks}>
          <div
            style={{
              fontSize: 12,
              color: "#595959",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            “{esc.remarks}”
          </div>
        </Tooltip>
      )}
      <div style={{ fontSize: 11, color: "#8c8c8c" }}>
        {/* Null when nobody raised it by hand — say so rather than leaving a blank where a name goes. */}
        {esc.raised_by || "the system"} · {fmtIST(esc.raised_at, "DD MMM HH:mm")}
      </div>
      {canResolve && (
        <span onClick={(e) => e.stopPropagation()}>
          <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onResolve(r)}>
            Mark resolved
          </Button>
        </span>
      )}
    </div>
  );
};

// Floor + lift line under the unloading pill — only for a customer delivery leg (the backend leaves
// delivery_floor/has_lift null for first-mile), and only when the order actually needs unloading.
// Module-level and exported: it reads nothing but the row, and the Vendor Dashboard renders the same
// line under its own Sales order cell rather than keeping a second copy.
// --- cells shared with the Missing Orders section ---------------------------------------------------
// Those rows are LEGS, not bookings (that section exists precisely because they have no truck), but
// they must render as board rows — same PO stack, same SO cell, same customer block. So the cells are
// exported and structurally typed on just the fields they read, and `buildColumns` below calls the
// same functions. One definition each: change it here and both surfaces move together, which is the
// whole reason the section is not a copy of this file.

export const poCell = (r: { po_numbers: string[]; cargo_weight_kg?: number | null }) =>
  r.po_numbers?.length ? (
    <div>
      {r.po_numbers.map((po) => (
        <CopyText key={po} value={po} label="PO number">
          <div style={{ fontWeight: 500, whiteSpace: "nowrap", color: "#1677ff" }}>{po}</div>
        </CopyText>
      ))}
      {typeof r.cargo_weight_kg === "number" && r.cargo_weight_kg > 0 && (
        <div style={{ fontSize: 11, color: "#8c8c8c", whiteSpace: "nowrap" }}>
          {r.cargo_weight_kg} kg
        </div>
      )}
    </div>
  ) : (
    dash(null)
  );

export const salesOrderCell = (r: {
  sales_order_id: number | null;
  zoho_salesorder_number: string | null;
  po_numbers: string[];
  is_express: boolean;
  is_unloading: boolean;
  is_transfer_chain: boolean;
  is_outstation?: boolean;
  delivery_floor: number | string | null;
  delivery_has_lift: boolean | null;
}) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ whiteSpace: "nowrap" }}>
      {r.sales_order_id ? (
        <Tooltip title="Click to copy SO number">
          <CopyText value={r.zoho_salesorder_number || String(r.sales_order_id)} label="SO number">
            <span style={{ color: "#1677ff" }}>
              {r.zoho_salesorder_number || `SO #${r.sales_order_id}`}
            </span>
          </CopyText>
        </Tooltip>
      ) : (
        dash(r.zoho_salesorder_number)
      )}
      {/* Straight through to the order page. A real anchor (antd renders `href` as one), so
          right-click → "open in new tab" works and the click does not also fire the row's
          contents modal.

          It deep-links to the allotment ONLY when this row carries exactly one PO — a
          consolidated truck spans several cards, and there is no single one to highlight. */}
      {r.sales_order_id ? (
        <Tooltip title="Open the OMS order details">
          <Button
            type="link"
            size="small"
            icon={<ExportOutlined />}
            href={omsOrderPath(r.sales_order_id, {
              poNumber: r.po_numbers?.length === 1 ? r.po_numbers[0] : null,
            })}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ padding: 0, height: "auto", fontSize: 12, marginLeft: 6 }}
          >
            Open
          </Button>
        </Tooltip>
      ) : null}
    </div>
    {(r.is_express || r.is_unloading || r.is_transfer_chain || r.is_outstation) && (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 3 }}>
        <ExpressTag show={r.is_express} />
        <UnloadingTag show={r.is_unloading} />
        {r.is_transfer_chain && (
          <Tooltip title="Goods cross warehouses on this order — one hop of a transfer chain">
            <Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
              Intercity
            </Tag>
          </Tooltip>
        )}
        {r.is_outstation && (
          <Tooltip title="Customer is outside every hub's zone (not a hub pincode, over 100 km from a hub)">
            <Tag color="orange" style={{ marginInlineEnd: 0 }}>
              Outstation client
            </Tag>
          </Tooltip>
        )}
      </div>
    )}
    {floorLift(r)}
  </div>
);

export const customerCell = (r: {
  client_name: string | null;
  client_contact: string | null;
  client_alternate_contact: string | null;
}) => (
  <div style={{ minWidth: 0 }}>
    <Tooltip title={r.client_name ?? undefined}>
      <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {r.client_name || <span style={{ color: "#bbb" }}>—</span>}
      </div>
    </Tooltip>
    {r.client_contact && (
      <div style={{ fontSize: 12, color: "#888" }}>
        <CopyText value={r.client_contact} label="phone">
          {r.client_contact}
        </CopyText>
      </div>
    )}
    {r.client_alternate_contact && (
      <div style={{ fontSize: 12, color: "#888" }}>
        <CopyText value={r.client_alternate_contact} label="alternate phone">
          Alt: {r.client_alternate_contact}
        </CopyText>
      </div>
    )}
  </div>
);

// Structurally typed for the same reason `escalationCell` is generic: the Missing Orders section's
// rows are legs, not bookings, and must render this identical line.
export const floorLift = (r: {
  is_unloading: boolean;
  delivery_floor: number | string | null;
  delivery_has_lift: boolean | null;
}) => {
  if (!r.is_unloading) return null;
  if (r.delivery_floor == null && r.delivery_has_lift == null) return null;
  const floor = r.delivery_floor != null ? `Floor ${r.delivery_floor}` : null;
  const lift = r.delivery_has_lift == null ? null : r.delivery_has_lift ? "Lift ✓" : "No lift";
  const text = [floor, lift].filter(Boolean).join(" · ");
  return text ? <div style={{ fontSize: 11, color: "#d46b08" }}>{text}</div> : null;
};

export interface ColumnHandlers {
  busyKey: string | null;
  setBusyKey: (k: string | null) => void;
  onSaved: (row: LogisticsOrderRow) => void;
  reload: () => void; // silent refetch after an action that returns nothing to merge
  onView: (r: LogisticsOrderRow) => void;
  onEdit: (r: LogisticsOrderRow) => void;
  onReassign: (r: LogisticsOrderRow) => void;
  onRaiseIssue: (r: LogisticsOrderRow) => void;
  onResolveIssue: (r: LogisticsOrderRow) => void; // closes the open escalation (back to its section)
  // May THIS user close an escalation out — the `oms.update` permission, which is what the server
  // enforces on the resolve endpoint (admin + the delivery and procurement teams). False while the
  // permission set is still loading, so the button never flashes for someone who may not have it.
  canResolveEscalation?: boolean;
  // May THIS user write to a booking at all — `oms.update`, the slug every per-row write endpoint
  // (status transitions, the Owner PATCH, edit, reassign, raise escalation, retry) defaults to.
  // Without it the row is read-only: Owner shows as text and only the read items stay in More.
  canUpdate?: boolean;
  onViewNotes: (r: LogisticsOrderRow) => void; // read-only: the thread, without raising/resolving
  showEscalation?: boolean; // mount the escalation-detail column (the escalation section's tables)
  teamMembers: TeamMember[]; // ops team for the Owner dropdown
  isAdmin?: boolean; // Delhivery column only renders for admin + delivery-team roles
  // `oms.driver_vehicle` — the Driver & vehicle column only renders with it. False while loading.
  canViewDriverVehicle?: boolean;
  onBookDelhivery?: (r: LogisticsOrderRow) => void; // opens the Delhivery booking / status modal
  onCancelDelhivery?: (r: LogisticsOrderRow) => void; // opens the cancel-reason prompt
}

export function buildColumns(h: ColumnHandlers): ColumnsType<LogisticsOrderRow> {
  const runAction = async (row: LogisticsOrderRow, action: AvailableAction) => {
    h.setBusyKey(`${row.id}:${action.action}`);
    const updated = await runLogisticsAction(action.href, action.label);
    h.setBusyKey(null);
    if (updated) h.onSaved(updated);
  };

  const confirmAction = (row: LogisticsOrderRow, action: AvailableAction) =>
    Modal.confirm({
      title: `${action.label}?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <span>
          This updates the shipment for everyone looking at this order.
          <br />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Booking #{row.id} · {row.client_name || "—"} · {row.origin_label || "—"} →{" "}
            {row.destination_label || "—"}
          </Typography.Text>
        </span>
      ),
      okText: action.label,
      cancelText: "Not yet",
      onOk: () => runAction(row, action),
    });

  const confirmMarkAttempted = (row: LogisticsOrderRow) => {
    let reason = "";
    Modal.confirm({
      title: `Mark delivery attempted — booking #${row.id}?`,
      icon: <ExclamationCircleOutlined />,
      width: 460,
      content: (
        <div>
          <span>
            Records that a delivery was tried and failed. The booking moves to{" "}
            <b>Attempted</b> and can then be reattempted.
          </span>
          <AttemptReason onChange={(v) => (reason = v)} />
        </div>
      ),
      okText: "Mark attempted",
      cancelText: "Not now",
      onOk: async () => {
        if (!reason.trim()) {
          message.error("A reason is required to mark a delivery attempted");
          return Promise.reject(); // keep the modal open
        }
        const updated = await markDeliveryAttempted(row.id, reason.trim());
        if (updated) h.onSaved(updated);
      },
    });
  };

  const confirmRequeue = (row: LogisticsOrderRow) =>
    Modal.confirm({
      title: `Retry booking #${row.id}?`,
      icon: <ExclamationCircleOutlined />,
      content: "The failed carrier call will be attempted again on the next pass.",
      okText: "Retry",
      cancelText: "Not now",
      onOk: async () => {
        const updated = await requeueBooking(row.id);
        if (updated) h.onSaved(updated);
        else h.reload();
      },
    });

  return [
    ...(h.showEscalation
      ? [
          {
            title: "Escalation",
            key: "escalation",
            width: 220,
            fixed: "left",
            render: (_: unknown, r: LogisticsOrderRow) =>
              escalationCell(r, h.onResolveIssue, !!h.canResolveEscalation),
          } as ColumnsType<LogisticsOrderRow>[number],
        ]
      : []),
    {
      // Allotment PO number(s). A consolidated truck carries several — show them all, stacked. Click
      // to copy.
      title: "PO #",
      key: "po",
      width: 140,
      fixed: "left",
      render: (_, r) => poCell(r),
    },
    {
      // The sales order number (click to copy), with the priority pills — and, for an unloading
      // delivery, the floor + lift — stacked BELOW it.
      title: "Sales order",
      key: "order",
      width: 200,
      fixed: "left",
      render: (_, r) => salesOrderCell(r),
    },
    {
      title: "Dispatch",
      key: "dispatch",
      width: 100,
      render: (_, r) => dispatchLabel(r),
    },
    {
      // One clean delivery status (created → booked → in_transit → delivered / cancelled), plus the
      // two work-queue overlays that answer a DIFFERENT question — what a human owes (attention) and
      // whether it's late (SLA).
      title: "Status",
      key: "status",
      width: 190,
      render: (_, r) => (
        <Space size={[4, 4]} wrap>
          <DeliveryStatusTag status={r.delivery_status} />
          <AttentionTag attention={r.attention} />
          <SlaTag sla={r.sla_state} />
          {/* A cancelled truck keeps its CLOSED section with an escalation still open on it — this
              tag is what explains such a row where the section cannot. */}
          <EscalationTag escalation={r.escalation} />
        </Space>
      ),
    },
    {
      // Pickup — just the label (truncated), linking to the precise pin we store.
      title: "From",
      key: "from",
      width: 150,
      render: (_, r) => locationCell(r.origin_label, r.origin_lat, r.origin_long),
    },
    {
      // Delivery — just the label (truncated), linking to the precise pin we store.
      title: "To",
      key: "to",
      width: 150,
      render: (_, r) => locationCell(r.destination_label, r.destination_lat, r.destination_long),
    },
    {
      // The customer this order is for — name (truncated) + phone (click to copy the number).
      title: "Customer",
      key: "customer",
      width: 160,
      render: (_, r) => customerCell(r),
    },
    {
      // Carrier — click the tag to copy its booking reference (provider_ref).
      title: "Carrier",
      dataIndex: "provider_type",
      width: 110,
      render: (p, r) => {
        const meta = CARRIER_META[p] || { label: p };
        const tag = (
          <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>
            {meta.label}
          </Tag>
        );
        return r.provider_ref ? (
          <Tooltip title="Click to copy carrier ref">
            <CopyText value={r.provider_ref} label="Carrier ref">
              {tag}
            </CopyText>
          </Tooltip>
        ) : (
          tag
        );
      },
    },
    ...(h.canViewDriverVehicle
      ? [
          {
            title: "Driver & vehicle",
            key: "driver",
            width: 150,
            render: (_: unknown, r: LogisticsOrderRow) => {
              const driver = [r.driver_name, r.driver_number].filter(Boolean).join(" · ");
              if (!r.vehicle_number && !driver)
                return <span style={{ color: "#bbb" }}>Not assigned</span>;
              return (
                <div>
                  <div>{dash(r.vehicle_number)}</div>
                  <div style={{ fontSize: 12, color: "#888" }}>{driver || "no driver yet"}</div>
                </div>
              );
            },
          },
        ]
      : []),
    {
      // Owner — a dropdown that sets/clears assigned_to from the ops team.
      title: "Owner",
      key: "owner",
      width: 140,
      render: (_, r) =>
        h.canUpdate ? (
          <OwnerCell row={r} teamMembers={h.teamMembers} onSaved={h.onSaved} />
        ) : (
          dash(r.assigned_to_name)
        ),
    },
    ...(h.isAdmin
      ? [
          {
            // Admin-only Delhivery booking entry. Booked → view live status; else eligible → book.
            title: "Delhivery",
            key: "delhivery",
            width: 250,
            render: (_: unknown, r: LogisticsOrderRow) => {
              if (!r.po_numbers?.length) return null;
              if (r.is_delhivery_booked) {
                return (
                  <Space size={4} wrap onClick={(e) => e.stopPropagation()}>
                    <Button size="small" onClick={() => h.onBookDelhivery?.(r)}>
                      See Delhivery Booking
                    </Button>
                    <Button
                      size="small"
                      danger
                      onClick={() => h.onCancelDelhivery?.(r)}
                    >
                      Cancel Delhivery booking
                    </Button>
                  </Space>
                );
              }
              if (r.is_delhivery_eligible) {
                return (
                  <span onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => h.onBookDelhivery?.(r)}
                    >
                      Book Delhivery
                    </Button>
                  </span>
                );
              }
              return null;
            },
          } as ColumnsType<LogisticsOrderRow>[number],
        ]
      : []),
    {
      title: "Action",
      key: "action",
      width: 240,
      render: (_, r) => (
        <Space size={4} wrap onClick={(e) => e.stopPropagation()}>
          {/* The server already sends no actions to a viewer without `oms.update`; this is the
              belt to its braces, for a row merged in from a response that didn't filter them. */}
          {(h.canUpdate ? r.available_actions ?? [] : []).map((a) => (
            <Button
              key={a.action}
              size="small"
              type={a.action === "mark_in_transit" ? "primary" : "default"}
              loading={h.busyKey === `${r.id}:${a.action}`}
              onClick={() => confirmAction(r, a)}
            >
              {a.label}
            </Button>
          ))}
          {h.canUpdate && r.attention === "failed" && (
            <Button size="small" danger onClick={() => confirmRequeue(r)}>
              Retry
            </Button>
          )}
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                { key: "view", label: "View all details" },
                ...(h.canUpdate
                  ? [
                      { key: "edit", label: "Edit / assign owner" },
                      ...(r.is_active
                        ? [{ key: "reassign", label: "Move to another carrier" }]
                        : []),
                      ...(isDeliveryLeg(r)
                        ? [{ key: "mark_attempted", label: "Mark delivery attempted" }]
                        : []),
                      { key: "issue", label: "Raise escalation", danger: true },
                    ]
                  : []),
                // Only when one is open: this is the way back out of the escalation section, and
                // offering it on an un-escalated truck would just 400. Gated the same way as the
                // column's Mark resolved — the two are one action and must not disagree.
                ...(r.escalation && h.canResolveEscalation
                  ? [{ key: "resolve", label: "Resolve escalation" }]
                  : []),
                // Read-only — the same thread Raise/Resolve already show, without committing to
                // either action. Only offered once the order actually has one to read.
                ...(r.sales_order_id ? [{ key: "notes", label: "View escalation notes" }] : []),
              ],
              onClick: ({ key }) => {
                if (key === "view") h.onView(r);
                else if (key === "edit") h.onEdit(r);
                else if (key === "reassign") h.onReassign(r);
                else if (key === "mark_attempted") confirmMarkAttempted(r);
                else if (key === "issue") h.onRaiseIssue(r);
                else if (key === "resolve") h.onResolveIssue(r);
                else if (key === "notes") h.onViewNotes(r);
              },
            }}
          >
            <Button size="small">More ▾</Button>
          </Dropdown>
        </Space>
      ),
    },
  ];
}
